import { roseStorage } from '@inboundx/shared/platform';
import { useCallback, useState } from 'react';

/**
 * A hook that provides useState-like API with automatic persistence to roseStorage.
 *
 * @param key - The storage key (will be prefixed with backoffice namespace internally)
 * @param defaultValue - The default value if no stored value exists
 * @returns A tuple of [value, setValue] similar to useState
 *
 * @example
 * // Simple value
 * const [theme, setTheme] = usePersistedState('theme', 'light');
 *
 * @example
 * // Object value
 * const [filters, setFilters] = usePersistedState('accounts-filters', {
 *     dateRange: '30days',
 *     sortBy: 'name',
 * });
 *
 * @example
 * // With updater function
 * setFilters(prev => ({ ...prev, dateRange: '7days' }));
 */
export function usePersistedState<T>(
    key: string,
    defaultValue: T,
    options?: {
        hydrate?: (storedValue: T, defaultValue: T) => T;
    }
): [T, (value: T | ((prev: T) => T)) => void] {
    const [state, setState] = useState<T>(() => {
        const stored = roseStorage.getBackofficePreference(key);
        if (stored) {
            try {
                const parsed = JSON.parse(stored) as T;
                return options?.hydrate ? options.hydrate(parsed, defaultValue) : parsed;
            } catch {
                // Fall through to default
            }
        }
        // Hydrate the default too: URL params and shared presets apply on a first visit.
        return options?.hydrate ? options.hydrate(defaultValue, defaultValue) : defaultValue;
    });

    const setPersistedState = useCallback(
        (updater: T | ((prev: T) => T)) => {
            setState((prev) => {
                const next =
                    typeof updater === 'function' ? (updater as (prev: T) => T)(prev) : updater;
                roseStorage.setBackofficePreference(key, JSON.stringify(next));
                return next;
            });
        },
        [key]
    );

    return [state, setPersistedState];
}

/**
 * A hook for persisting Set<string> state (e.g., visible columns).
 * Provides Set-based API while storing as array in roseStorage.
 *
 * @param key - The storage key
 * @param defaultValue - The default Set if no stored value exists
 * @returns A tuple of [Set, toggle function, set function]
 *
 * @example
 * const [visibleColumns, toggleColumn, setVisibleColumns] = usePersistedSet(
 *     'accounts-columns',
 *     new Set(['name', 'email', 'status'])
 * );
 *
 * // Toggle a column
 * toggleColumn('phone');
 *
 * // Replace entire set
 * setVisibleColumns(new Set(['name', 'email']));
 */
export function usePersistedSet(
    key: string,
    defaultValue: Set<string>
): [Set<string>, (item: string) => void, (value: Set<string>) => void] {
    const [state, setState] = useState<Set<string>>(() => {
        const stored = roseStorage.getBackofficePreference(key);
        if (stored) {
            try {
                return new Set(JSON.parse(stored) as string[]);
            } catch {
                // Fall through to default
            }
        }
        return defaultValue;
    });

    const toggle = useCallback(
        (item: string) => {
            setState((prev) => {
                const next = new Set(prev);
                if (next.has(item)) {
                    next.delete(item);
                } else {
                    next.add(item);
                }
                roseStorage.setBackofficePreference(key, JSON.stringify([...next]));
                return next;
            });
        },
        [key]
    );

    const setSet = useCallback(
        (value: Set<string>) => {
            setState(value);
            roseStorage.setBackofficePreference(key, JSON.stringify([...value]));
        },
        [key]
    );

    return [state, toggle, setSet];
}
